import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { onAuthStateChanged } from "firebase/auth";
import { useEffect } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { PrivateRoute } from "@/components/auth/PrivateRoute";
import { api } from "@/lib/api/client";
import { auth } from "@/lib/firebase/config";
import AdminPage from "@/pages/admin/AdminPage";
import DrawPage from "@/pages/DrawPage";
import HomePage from "@/pages/HomePage";
import IcebreakerPage from "@/pages/IcebreakerPage";
import JoinPage from "@/pages/JoinPage";
import LoginPage from "@/pages/LoginPage";
import MenuboardsPage from "@/pages/MenuboardsPage";
import NotFoundPage from "@/pages/NotFoundPage";
import OrderResultPage from "@/pages/OrderResultPage";
import PeoplePage from "@/pages/PeoplePage";
import PublicOrderPage from "@/pages/PublicOrderPage";
import SettingsPage from "@/pages/SettingsPage";
import ToastPage from "@/pages/ToastPage";

const queryClient = new QueryClient();

/** 로그인 상태가 잡히면 한 번 내 이름·이메일을 서버에 남긴다.
 *
 * 앱 최상단에 두는 이유: `PrivateRoute` 안에 넣으면 보호된 화면을 옮겨 다닐 때마다
 * 다시 붙어 같은 기록을 반복해서 쓴다. 여기 useEffect 는 앱 수명 동안 한 번만 건다.
 *
 * 실패는 삼킨다. 이건 표시용 사본을 남기는 곁가지지 인증 경로가 아니다 — 여기서
 * 넘어진다고 주문을 못 받게 만들면 안 된다. 인증 자체는 계속 Firebase 가 한다.
 */
function useRecordSignIn() {
  useEffect(
    () =>
      onAuthStateChanged(auth, (user) => {
        if (user) void api.put("/users/me").catch(() => undefined);
      }),
    []
  );
}

export default function App() {
  useRecordSignIn();

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/o/:slug" element={<PublicOrderPage />} />
          <Route
            path="/join/:code"
            element={
              <PrivateRoute>
                <JoinPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/"
            element={
              <PrivateRoute>
                <HomePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/people"
            element={
              <PrivateRoute>
                <PeoplePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/menuboards"
            element={
              <PrivateRoute>
                <MenuboardsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/toast"
            element={
              <PrivateRoute>
                <ToastPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/icebreaker"
            element={
              <PrivateRoute>
                <IcebreakerPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/draw"
            element={
              <PrivateRoute>
                <DrawPage />
              </PrivateRoute>
            }
          />
          {/* 탭 노출 설정. 탭을 전부 감춰도 헤더의 입구로 여기 올 수 있다 —
              라우트는 감춤과 무관하게 늘 살아 있다(감춤은 권한이 아니다). */}
          <Route
            path="/settings"
            element={
              <PrivateRoute>
                <SettingsPage />
              </PrivateRoute>
            }
          />
          {/* 운영자 전용. AppShell 탭에는 노출하지 않고 주소를 아는 사람만 들어온다. */}
          <Route
            path="/admin"
            element={
              <PrivateRoute>
                <AdminPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/orders/:orderId"
            element={
              <PrivateRoute>
                <OrderResultPage />
              </PrivateRoute>
            }
          />
          {/* 없는 주소. 로그인 뒤로 감싸지 않는다 — 오래된 주문 링크를 받은
              바깥 사람도 흰 화면 대신 안내를 봐야 한다. */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" />
    </QueryClientProvider>
  );
}
